import React from 'react';

type SpinnerProps = {
  readonly size?: 'sm' | 'md' | 'lg';
  readonly speed?: string;
  readonly track?: string;
  readonly from?: string;
  readonly to?: string;
  readonly label?: string;
  readonly className?: string;
};

export function Loading({
  size = 'lg',
  speed,
  track,
  from = '#5C5FFF',
  to = 'rgba(92,95,255,0)',
  label = 'Wird geladen …',
  className = ''
}: SpinnerProps) {
  const style: React.CSSProperties = {
    ...(speed ? { ['--spinner-speed' as keyof React.CSSProperties]: speed } : {}),
    ...(track ? { ['--spinner-track' as keyof React.CSSProperties]: track } : {})
  };
  const containerStyle = {
    height: '100%',
    display: 'flex',
    alignItems: 'center'
  };

  const wrapperStyle = {
    margin: 'auto'
  };

  return (
    <>
      <style>
        {`
          :root {
            --spinner-size: 56px;
            --spinner-thickness: 8px;
            --spinner-track: #e2e6ee;
            --spinner-speed: 1s;
            --circumference: 163.3628; /* 2πr für r=26 */
          }

          .spinner {
            display: inline-block;
            width: var(--spinner-size);
            height: var(--spinner-size);
            animation: rotate var(--spinner-speed) linear infinite;
            transform-origin: 50% 50%;
          }

          .spinner circle.bg {
            stroke: var(--spinner-track);
            stroke-width: var(--spinner-thickness);
            fill: none;
          }

          .spinner circle.fg {
            stroke-width: var(--spinner-thickness);
            stroke-linecap: round;
            fill: none;
            stroke-dasharray: 90 164;
          }

          @keyframes dash-in {
            0% {
                stroke-dasharray: 0 var(--circumference);
            }
            60% {
                stroke-dasharray: 90 var(--circumference);
            }
            100% {
                stroke-dasharray: 120 var(--circumference);
            }
          }

          [data-size='sm'] {
            --spinner-size: 20px;
            --spinner-thickness: 4px;
          }
          [data-size='md'] {
            --spinner-size: 32px;
            --spinner-thickness: 6px;
          }
          [data-size='lg'] {
            --spinner-size: 56px;
            --spinner-thickness: 8px;
          }

          @media (prefers-color-scheme: dark) {
            .spinner-overlay {
                background: rgba(0, 0, 0, 0.5);
            }
            .spinner-text {
                color: #e5e7eb;
            }
            :root {
                --spinner-track: rgba(255, 255, 255, 0.2);
            }
          }

          @media (prefers-reduced-motion: reduce) {
            .spinner {
                animation: none;
            }
            .variant-3 {
                animation: none;
            }
            .variant-3 circle.fg {
                animation: none;
            }
          }

          @keyframes rotate-ease {
            0% {
                transform: rotate(0deg);
            }
            99.8% {
                transform: rotate(360deg);
            }
            100% {
                transform: rotate(360deg);
            } /* ~0.2% Stop */
          }

          .variant-3 {
            animation: rotate-ease 1.2s cubic-bezier(0.76, 0, 0.24, 1) infinite;
            transform-origin: 50% 50%;
          }

          .variant-3 circle.fg {
            stroke: url(#spinner-gradient);
            stroke-dasharray: 0 var(--circumference);
            animation: dash-in 1s ease-out forwards;
          }
      `}
      </style>
      <div style={containerStyle}>
        <div style={wrapperStyle}>
          <output
            data-size={size}
            className={className}
            aria-label={label}
            aria-live="polite"
            style={style}
          >
            <svg
              viewBox="0 0 60 60"
              width="100%"
              height="100%"
              className="spinner variant-3"
              focusable="false"
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="spinner-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={from} stopOpacity="1" />
                  <stop offset="100%" stopColor={to} stopOpacity="1" />
                </linearGradient>
              </defs>
              <circle className="bg" cx="30" cy="30" r="26" />
              <circle className="fg" cx="30" cy="30" r="26" />
            </svg>
          </output>
        </div>
      </div>
    </>
  );
}
